import classnames from 'classnames';
import React, {useId} from 'react';

import {IconButton, Popover} from '@albert-io/atomic';
import {useHoverPopover} from 'lib/hooks/useHoverPopover';
import {upperFirst} from 'lib/stringUtils';

import {isSupplementAnchor} from '../engine/annotationAnchors';
import type {MountedSupplement} from '../store/AnnotationModeStore';
import {useAnnotationModeStore} from '../store/AnnotationModeStore';
import AnnotationNotePeek, {peekTabbables} from './AnnotationNotePeek';
import {ANNOTATION_SHIELD_EXEMPT_PROPS} from '../shared/annotationFreeze';
import {HIGHLIGHT_BUTTON_CLASS_BY_COLOR} from '../shared/annotationHighlightStyles';
import type {AnnotationNoteLine} from './annotationNoteLines';
import {selectIndicatorColor, supplementNoteLabel} from '../store/annotationSelectors';
import type {Anchor, Annotation, HighlightColor} from '../types';

interface AnnotationNoteIndicatorProps {
  line: AnnotationNoteLine;
  /**
   * What `line.center` measures: the middle of a line box (the default, which pulls the
   * button up half its height) or the top edge of a marked element, which it hangs from.
   */
  align?: 'center' | 'top';
  /** The fill, where only some of the line's marks are painted; the line's own color otherwise. */
  color?: HighlightColor;
}

const QUOTE_LIST_FORMAT = new Intl.ListFormat('en', {style: 'long', type: 'conjunction'});

/** What a note is on, as its peek heads it: a text mark's words, or a supplement's label. */
const quoteOf = (anchor: Anchor, mountedSupplements: MountedSupplement[]): string =>
  isSupplementAnchor(anchor)
    ? supplementNoteLabel(mountedSupplements, anchor.regionId)
    : `“${anchor.exact.trim()}”`;

/** "note on “a”" or "2 notes on “a” and “b”": what the indicator and its peek are named for. */
const notesOn = (annotations: Annotation[], mountedSupplements: MountedSupplement[]): string => {
  const quotes = QUOTE_LIST_FORMAT.format(
    annotations.map(({anchor}) => quoteOf(anchor, mountedSupplements))
  );
  return annotations.length === 1
    ? `note on ${quotes}`
    : `${annotations.length} notes on ${quotes}`;
};

/** What the peek shows, read out after the name; a lone note's quote is in the name already. */
const indicatorDescription = (
  annotations: Annotation[],
  mountedSupplements: MountedSupplement[]
): string =>
  annotations.length === 1
    ? (annotations[0].note ?? '')
    : annotations
        .map(({anchor, note}) => `${quoteOf(anchor, mountedSupplements)}: ${note ?? ''}`)
        .join('; ');

/**
 * The margin button for one line's notes, filled with the color of the highlight it
 * flags and named for the text they are on. Hover peeks at them. Where there is a notes
 * panel, focus peeks too and activating opens the panel; where there is none, activating
 * toggles the peek. `top` is inline because it is measured from rendered text. It takes
 * no z-index, so the pane's sticky chrome scrolls over it as it does the text.
 */
export default function AnnotationNoteIndicator({
  line,
  align = 'center',
  color: colorOverride
}: AnnotationNoteIndicatorProps) {
  const color = colorOverride ?? selectIndicatorColor(line.annotations);
  const {isOpen, close, triggerRef, triggerProps, contentProps} = useHoverPopover();
  const peekId = useId();
  const descriptionId = useId();
  const isNotesPanelOpen = useAnnotationModeStore((state) => state.isNotesPanelOpen);
  const isReadOnly = useAnnotationModeStore((state) => state.isReadOnly);
  const openNotesPanel = useAnnotationModeStore((state) => state.openNotesPanel);
  const mountedSupplements = useAnnotationModeStore((state) => state.mountedSupplements);
  const notes = notesOn(line.annotations, mountedSupplements);
  const {
    'aria-expanded': isPeekExpanded,
    onFocus: openPeek,
    onClick: togglePeek,
    ...hoverProps
  } = triggerProps;

  return (
    <>
      <IconButton
        {...hoverProps}
        // Not the hook's toggle where there is a panel: every indicator opens the one panel,
        // so one line's could close another's notes. Read-only toggles; focus leaves it shut.
        {...(isReadOnly
          ? {'aria-expanded': isPeekExpanded, onClick: togglePeek}
          : {
              onFocus: openPeek,
              // The panel shows every note, and a peek left open would take its Escape.
              onClick: () => {
                close();
                openNotesPanel(line.annotations[0].id);
              }
            })}
        onKeyDown={(event: React.KeyboardEvent) => {
          hoverProps.onKeyDown(event);
          // Portaled, the peek follows the indicator in tab order only if focus is handed in.
          const [first] =
            event.key === 'Tab' && !event.shiftKey && isOpen
              ? peekTabbables(document.getElementById(peekId))
              : [];
          if (first) {
            event.preventDefault();
            first.focus();
          }
        }}
        aria-describedby={descriptionId}
        aria-disabled={isNotesPanelOpen}
        icon='sticky-note'
        iconStyle='regular'
        label={`Show ${notes}`}
        size='xs'
        className={classnames(
          'right-x0-half absolute',
          align === 'center' && '-translate-y-1/2',
          HIGHLIGHT_BUTTON_CLASS_BY_COLOR[color],
          {'cursor-default': isNotesPanelOpen}
        )}
        style={{top: line.center}}
        data-testid='annotation-note-indicator'
        {...ANNOTATION_SHIELD_EXEMPT_PROPS}
      />
      <span id={descriptionId} hidden>
        {indicatorDescription(line.annotations, mountedSupplements)}
      </span>
      {isOpen && (
        // Opens inward, since the trigger sits against the pane's right edge.
        <Popover
          targetRef={triggerRef}
          expanded
          optimizeForMobile={false}
          position='left-start'
          shadow='default'
          paddingLevel={0}
        >
          <AnnotationNotePeek
            id={peekId}
            label={upperFirst(notes)}
            line={line}
            triggerRef={triggerRef}
            contentProps={contentProps}
            onClose={close}
          />
        </Popover>
      )}
    </>
  );
}
